import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProjectInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    requirements: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const projectTypes = [
    { value: 'web-app', label: 'Web Application' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'custom-software', label: 'Custom Software Development' },
    { value: 'ui-ux', label: 'UI/UX Design' },
    { value: 'consultation', label: 'Technical Consultation' },
    { value: 'maintenance', label: 'Maintenance & Support' }
  ];

  const budgetRanges = [
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k+', label: '$100,000+' },
    { value: 'discuss', label: 'Let\'s Discuss' }
  ];

  const timelines = [
    { value: 'asap', label: 'ASAP (Rush Project)' },
    { value: '1-3months', label: '1-3 Months' },
    { value: '3-6months', label: '3-6 Months' },
    { value: '6months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const requirements = [
    'Responsive Design',
    'Mobile App',
    'Database Integration',
    'Payment Processing',
    'User Authentication',
    'API Development',
    'Third-party Integrations',
    'Analytics & Reporting',
    'Content Management',
    'E-commerce Features'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequirementToggle = (requirement) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev?.requirements?.includes(requirement)
        ? prev?.requirements?.filter(r => r !== requirement)
        : [...prev?.requirements, requirement]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Form submitted:', formData);
    setIsSubmitting(false);
    
    // Reset form or show success message
    alert('Thank you! We\'ll get back to you within 2 hours.');
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Project Inquiry
            <span className="text-accent"> Configurator</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Tell us about your vision, and we'll craft a tailored solution that exceeds your expectations.
          </p>
        </motion.div>

        {/* Progress Indicator */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-4">
            {[1, 2, 3]?.map((step) => (
              <React.Fragment key={step}>
                <motion.div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    currentStep >= step
                      ? 'bg-accent border-accent text-accent-foreground'
                      : 'border-border text-muted-foreground'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  animate={{
                    scale: currentStep === step ? 1.1 : 1,
                    boxShadow: currentStep === step ? "0 0 20px rgba(0, 212, 255, 0.5)" : "none"
                  }}
                >
                  {currentStep > step ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <span className="text-sm font-bold">{step}</span>
                  )}
                </motion.div>
                {step < 3 && (
                  <div className={`w-16 h-0.5 ${
                    currentStep > step ? 'bg-accent' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form */}
        <motion.div
          className="bg-card border border-border rounded-3xl p-8 shadow-3d"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-6"
              >
                <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                  <Icon name="User" size={24} className="text-accent mr-3" />
                  Basic Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="John Doe"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    required
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="john@company.com"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    required
                  />
                  
                  <Input
                    label="Company Name"
                    type="text"
                    placeholder="Your Company"
                    value={formData?.company}
                    onChange={(e) => handleInputChange('company', e?.target?.value)}
                  />
                  
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  />
                </div>
              </motion.div>
            )}

            {/* Step 2: Project Details */}
            {currentStep === 2 && (
              <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-6"
              >
                <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                  <Icon name="Settings" size={24} className="text-accent mr-3" />
                  Project Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Select
                    label="Project Type"
                    options={projectTypes}
                    value={formData?.projectType}
                    onChange={(value) => handleInputChange('projectType', value)}
                    placeholder="Select project type"
                    required
                  />
                  
                  <Select
                    label="Budget Range"
                    options={budgetRanges}
                    value={formData?.budget}
                    onChange={(value) => handleInputChange('budget', value)}
                    placeholder="Select budget range"
                    required
                  />
                  
                  <Select
                    label="Timeline"
                    options={timelines}
                    value={formData?.timeline}
                    onChange={(value) => handleInputChange('timeline', value)}
                    placeholder="Select timeline"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-4">
                    Project Requirements
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {requirements?.map((requirement) => (
                      <motion.button
                        key={requirement}
                        type="button"
                        onClick={() => handleRequirementToggle(requirement)}
                        className={`p-3 rounded-lg border text-sm font-medium transition-all duration-300 ${
                          formData?.requirements?.includes(requirement)
                            ? 'bg-accent/10 border-accent text-accent' :'bg-muted/30 border-border text-muted-foreground hover:border-accent/50'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {requirement}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Project Description */}
            {currentStep === 3 && (
              <motion.div
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="space-y-6"
              >
                <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                  <Icon name="FileText" size={24} className="text-accent mr-3" />
                  Project Description
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tell us about your project
                  </label>
                  <textarea
                    className="w-full h-40 p-4 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                    placeholder="Describe your project goals, target audience, key features, and any specific requirements or challenges you'd like us to address..."
                    value={formData?.description}
                    onChange={(e) => handleInputChange('description', e?.target?.value)}
                    required
                  />
                </div>

                <div className="bg-muted/30 rounded-lg p-6">
                  <h4 className="font-semibold text-foreground mb-4 flex items-center">
                    <Icon name="Info" size={18} className="text-accent mr-2" />
                    What happens next?
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center space-x-2">
                      <Icon name="Clock" size={14} className="text-success" />
                      <span>We'll review your inquiry within 2 hours</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Icon name="Phone" size={14} className="text-success" />
                      <span>Schedule a free 30-minute consultation call</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Icon name="FileText" size={14} className="text-success" />
                      <span>Receive a detailed project proposal within 48 hours</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 1}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>

              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  iconName="ChevronRight"
                  iconPosition="right"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  type="submit"
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                </Button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectInquiryForm;